import AddSharpIcon from "@mui/icons-material/AddSharp";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import { IconButton, Typography } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import { DataGrid, GridColDef, GridRowId } from "@mui/x-data-grid";
import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router";
import { usePersistentState, markNavigationPreserve, consumeNavigationPreserve, clearNamespace } from "../../hooks/persistence";
import { Service } from "../../Service/ApiService";
import { CommonService } from "../../Service/CommonService";
import "../Dynamic/UserList.scss";
import EditIcon from "../../assets/images/icons/EditIcon.svg";
import DeleteOwner from "../popups/DeleteOwner";
import DataGridLoader from "../Common/DataGridLoader";

const BarrelOwnerListing = () => {
  const location = useLocation();
  const currentPath = location.pathname;
  const { id } = useParams<{ id: string }>();
  const [tableData, setTableData] = useState<any[]>([]);
  let active: any[] = [];
  let deleted: any[] = [];
  const disableAction = useRef(false);
  const isNeedReviewClicked = useRef(false);
  const selectedRows = useRef(active);
  const [selectionRowsFromTable, setSelectionRowsFromTable] = useState<any[]>(
    []
  );
  const history = useHistory();
  const [activeOwnerCount, setActiveOwnerCount] = useState(0);
  const [deleteOwnerCount, setDeleteOwnerCount] = useState(0);
  const isActiveOwnerClicked = useRef(false);
  const isDeletedOwnerClicked = useRef(false);
  const [totalCount, setTotalCount] = useState(0);
  const controllerRef = useRef<AbortController | null>(null);

  const initialFilter = {
    ownerName: null,
    email: null,
    phone: null,
    location: null,
    totalInventories: null,
  };

  const _filterInitRef = useRef(false);
  if (!_filterInitRef.current) {
    _filterInitRef.current = true;
    if (!consumeNavigationPreserve("barrel-owners")) {
      clearNamespace("barrel-owners");
    }
  }

  const [page, setPage] = usePersistentState<number>("barrel-owners", "page", 0);
  const [pageSize, setPageSize] = usePersistentState<number>("barrel-owners", "pageSize", 50);
  const [sortField, setSortField] = usePersistentState<string>("barrel-owners", "sortField", "createdDate");
  const [sortDirection, setSortDirection] = usePersistentState<string>("barrel-owners", "sortDirection", "asc");
  const [filter, setFilter] = usePersistentState<any>("barrel-owners", "filter", initialFilter);
  const [selectionModel, setSelectionModel] = React.useState<GridRowId[]>([]);

  const reloadTable = () => {
    if (isActiveOwnerClicked.current) {
      fetchActiveOwnerData(
        page,
        pageSize,
        filter,
        sortField,
        sortDirection,
        false
      );
      fetchActiveOwnerCount();
      fetchDeleteOwnerCount();
    } else {
      fetchDeletedOwnerData(
        page,
        pageSize,
        filter,
        sortField,
        sortDirection,
        false
      );
      fetchActiveOwnerCount();
      fetchDeleteOwnerCount();
    }
  };

  const showActiveOwners = () => {
    //write the logic for show active users
    if (!isActiveOwnerClicked.current) {
      isDeletedOwnerClicked.current = false;
      isActiveOwnerClicked.current = true;
      fetchActiveOwnerData(
        page,
        pageSize,
        filter,
        sortField,
        sortDirection,
        true
      );
    }
  };

  const showDeletedUsers = () => {
    //write the logic for show active users
    if (!isDeletedOwnerClicked.current) {
      isActiveOwnerClicked.current = false;
      isDeletedOwnerClicked.current = true;
      fetchDeletedOwnerData(
        page,
        pageSize,
        filter,
        sortField,
        sortDirection,
        true
      );
    }
  };

  //redirect to the add user page
  const handleAddOwner = () => {
    if (CommonService.isAdmin()) history.push("/master/add-owner", { fromInternal: true });
    else history.push("/user/add-owner", { fromInternal: true });
  };

  const fetchActiveOwnerData = async (page: any, size: any, filter: any, sortField: any, sortDirection: any, isSearching: any)  => {
    const controller = new AbortController();
    const signal = controller.signal;
    try {
      setIsLoading(true);
      setTableData([]);
      // Cancel the previous request if any
      if (controllerRef.current) {
        controllerRef.current.abort();
      }

      // Store the current controller to cancel the request if needed
      controllerRef.current = controller;
      isActiveOwnerClicked.current = true;

      const response = await Service.fetchAllActiveOwners(
        CommonService.isUser() ? CommonService.getCompanyId() : null,
        page,
        size,
        filter,
        sortField,
        sortDirection,
        signal
      );
      active = Object.values(response.data.content); // Convert object to array
      setTableData(response.data.content);
      setTotalCount(response.data.totalElements);
      if (!isSearching) {
        setActiveOwnerCount(response.data.totalElements);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDeletedOwnerData = async (
    page: any,
    size: any,
    filter: any,
    sortField: any,
    sortDirection: any,
    isSearching: any
  ) => {
    const controller = new AbortController();
    const signal = controller.signal;
    try {
      setIsLoading(true);
      setTableData([]);
      // Cancel the previous request if any
      if (controllerRef.current) {
        controllerRef.current.abort();
      }

      // Store the current controller to cancel the request if needed
      controllerRef.current = controller;
      isDeletedOwnerClicked.current = true;

      const response = await Service.fetchAllDeletedOwners(
        CommonService.isUser() ? CommonService.getCompanyId() : null,
        page,
        size,
        filter,
        sortField,
        sortDirection,
        signal
      );
      deleted = Object.values(response.data.content); // Convert object to array
      setTableData(response.data.content);
      setTotalCount(response.data.totalElements);
      if (!isSearching) {
        setDeleteOwnerCount(response.data.totalElements);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchActiveOwnerCount = async () => {
    try {
      const response = await Service.fetchActiveOwnersCount(CommonService.isUser() ? CommonService.getCompanyId() : null);
      setActiveOwnerCount(response.data);
    } catch (error) {
      console.error("Error fetching counts:", error);
    }
  };

  const fetchDeleteOwnerCount = async () => {
    try {
      const response = await Service.fetchDeletedOwnerCount(CommonService.isUser() ? CommonService.getCompanyId() : null);
      setDeleteOwnerCount(response.data);
    } catch (error) {
      console.error("Error fetching counts:", error);
    }
  };

  const handleEditOwner = (id: any) => {
    markNavigationPreserve("barrel-owners");
    history.push(
      CommonService.isAdmin()
        ? "/master/update-owner/"
        : "/user/update-owner/" + id,
      { fromInternal: true }
    );
  };

  useEffect(() => {
    fetchActiveOwnerData(
      page,
      pageSize,
      filter,
      sortField,
      sortDirection,
      false
    );
    fetchDeleteOwnerCount();
  }, []);

  //add tooltip
  const CellWithTooltip = ({ value }: { value: string | number }) => {
    return (
      <Tooltip arrow title={value?.toString()}>
        <span>{value?.toString()}</span>
      </Tooltip>
    );
  };

  const handleFilterChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    field: keyof any
  ) => {
    const value = event.target.value;

    // Prepare a temporary filter object with the updated field value
    const tempFilter = {
      ...filter, // Spread existing filter state
      [field]: value, // Update field only if the condition is met
    };

    // Update the filter state
    setFilter(tempFilter);

    // Load the service requests with updated filters
    if (isActiveOwnerClicked.current) {
      fetchActiveOwnerData(
        page,
        pageSize,
        tempFilter,
        sortField,
        sortDirection,
        true
      );
    } else {
      fetchDeletedOwnerData(
        page,
        pageSize,
        tempFilter,
        sortField,
        sortDirection,
        true
      );
    }
  };

  const columns: GridColDef[] = [
    {
      field: "ownerName",
      headerName: "Owner Name",
      flex: 1,
      minWidth: 200,
      sortable: true,
      valueGetter: (value: any, row: any) => {
        return row.ownerName || "";
      },
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <div>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <div className="position-relative" style={{ width: 170 }}>
            <input
              type="text"
              className="form-control border mt-1"
              style={{ textIndent: "24px", boxShadow: "none" }}
              placeholder="Search"
              defaultValue={filter.ownerName}
              onChange={(event) => handleFilterChange(event, "ownerName")}
              onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
            />
            <SearchIcon
              sx={{
                color: "#6F6F6F",
                position: "absolute",
                top: "50%",
                left: "8px",
                transform: "translateY(-50%)",
              }}
            />
          </div>
        </div>
      ),
      renderCell: (params: any) => <div>{params.value}</div>,
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      minWidth: 200,
      sortable: true,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <div>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <div className="position-relative" style={{ width: 170 }}>
            <input
              type="text"
              className="form-control border mt-1"
              style={{ textIndent: "24px", boxShadow: "none" }}
              placeholder="Search"
              defaultValue={filter.email}
              onChange={(event) => handleFilterChange(event, "email")}
              onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
            />
            <SearchIcon
              sx={{
                color: "#6F6F6F",
                position: "absolute",
                top: "50%",
                left: "8px",
                transform: "translateY(-50%)",
              }}
            />
          </div>
        </div>
      ),
      renderCell: (params) => <CellWithTooltip value={params.value || "-"} />,
    },
    {
      field: "phone",
      headerName: "Phone",
      flex: 1,
      minWidth: 200,
      sortable: true,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <div>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <div className="position-relative" style={{ width: 170 }}>
            <input
              type="text"
              className="form-control border mt-1"
              style={{ textIndent: "24px", boxShadow: "none" }}
              placeholder="Search"
              defaultValue={filter.phone}
              onChange={(event) => handleFilterChange(event, "phone")}
              onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
            />
            <SearchIcon
              sx={{
                color: "#6F6F6F",
                position: "absolute",
                top: "50%",
                left: "8px",
                transform: "translateY(-50%)",
              }}
            />
          </div>
        </div>
      ),
      renderCell: (params) => <CellWithTooltip value={params.value || "-"} />,
    },
    {
      field: "location",
      headerName: "Location",
      flex: 1,
      minWidth: 200,
      sortable: true,
      headerClassName: "table-header border-0 my-2 h-100",
      renderHeader: (params) => (
        <div>
          <Typography
            variant="body2"
            sx={{ color: "#6F6F6F", fontSize: "14px" }}
          >
            {params.colDef.headerName}
          </Typography>
          <div className="position-relative" style={{ width: 170 }}>
            <input
              type="text"
              className="form-control border mt-1"
              style={{ textIndent: "24px", boxShadow: "none" }}
              placeholder="Search"
              defaultValue={filter.location}
              onChange={(event) => handleFilterChange(event, "location")}
              onClick={(event) => event.stopPropagation()} // Prevent event from bubbling up
            />
            <SearchIcon
              sx={{
                color: "#6F6F6F",
                position: "absolute",
                top: "50%",
                left: "8px",
                transform: "translateY(-50%)",
              }}
            />
          </div>
        </div>
      ),
      renderCell: (params) => <CellWithTooltip value={params.value || "-"} />,
    },
    {
      field: "action",
      headerName: "Action",
      sortable: false,
      width: 200,
      headerClassName: "table-header border-0 my-2 h-100",
      renderCell: (params: any) => (
        <>
          {isActiveOwnerClicked.current && (
            <Tooltip title="Edit" arrow>
              <IconButton
                aria-label="message"
                size="small"
                onClick={() => handleEditOwner(params?.row?.id)}
              >
                <img src={EditIcon} className="cursor-pointer" alt="EditIcon" />
              </IconButton>
            </Tooltip>
          )}
        </>
      ),
    },
  ];

  const handleSelectionModelChange = (newSelection: GridRowId[]) => {
    setSelectionModel(newSelection);
    const newSelectedRows = newSelection.map((id: GridRowId) => {
      return tableData.find((row: any) => row.id === id); // Assuming each row object has an 'id' property
    });
    setSelectionRowsFromTable(newSelectedRows);
    selectedRows.current = newSelectedRows;
    let flag: any = false;
    flag = selectedRows.current.some((user) => user.userStatus === "INVITED");
    disableAction.current = flag;
  };

  const [isLoading, setIsLoading] = useState(false);
  // Define the localeText object based on the isLoading flag
  const localeText = {
    noRowsLabel: isLoading ? "Fetching records ..." : "No Record Found",
  };

  // Map filtered rows with unique IDs
  const tableDataWithIds = tableData?.map((rowData, index) => ({
    ...rowData,
  }));

  const handleBack = () => {
    if (currentPath.startsWith("/master/my-warehouse/user-list/")) {
      history.push(`/master/company-dashboard/` + id);
    } else history.push(`/master/dashboard`);
  };

  const onPaginationModelChange = (newPaginationModel) => {
    setPage(newPaginationModel.page);
    setPageSize(newPaginationModel.pageSize);

    if (isActiveOwnerClicked.current) {
      fetchActiveOwnerData(
        newPaginationModel.page,
        newPaginationModel.pageSize,
        filter,
        sortField,
        sortDirection,
        true
      );
    } else {
      fetchDeletedOwnerData(
        newPaginationModel.page,
        newPaginationModel.pageSize,
        filter,
        sortField,
        sortDirection,
        true
      );
    }
  };

  const handleSortChange = (sortModel) => {
    if (sortModel.length > 0) {
      const newSortField = sortModel[0].field;
      const newSortDirection = sortModel[0].sort; // "asc" or "desc" directly from DataGrid

      setSortField(newSortField);
      setSortDirection(newSortDirection);

      if (isActiveOwnerClicked.current) {
        fetchActiveOwnerData(
          page,
          pageSize,
          filter,
          newSortField,
          newSortDirection,
          true
        );
      } else {
        fetchDeletedOwnerData(
          page,
          pageSize,
          filter,
          newSortField,
          newSortDirection,
          true
        );
      }
    }
  };

  return (
    <>
      <div className="usersList ownerList">
        <div className="usersList__header--wrapper d-flex flex-wrap justify-content-between align-items-center gap-2">
          <div className="d-flex align-items-center flex-wrap gap-2">
            {CommonService.isAdmin() && (
              <IconButton
                onClick={handleBack}
                aria-label="ArrowBackIcon"
                sx={{
                  backgroundColor: "#FFF",
                  "&:hover": { backgroundColor: "#FFF" },
                }}
              >
                <ArrowBackIcon sx={{ color: "#1C1B1F" }} />
              </IconButton>
            )}
            {CommonService.isAdmin() && (
              <>
              <div className="header--title">
                <h1 className="title mb-0">
                  Owners <i className="bi bi-heart-fill"></i>
                </h1>
              </div>
              <div className="divider mx-2"></div>
              </>
            )}
            <button
                  className="primary-btn d-inline-flex"
                  onClick={handleAddOwner}
                >
                  <AddSharpIcon />
                  Add Owner
                </button>
          </div>
          <div className="header--menu ms-auto">
            <div className="header--menu-items">
              {/* {selectionModel.length > 0 && !isDeletedOwnerClicked.current && (
                <div className="col-auto mb-2">
                  <DeleteOwner
                    deleteOwner={selectionModel}
                    reloadTable={reloadTable}
                  />
                </div>
              )} */}
              <div className="menu-items__btnGroup btnGroup-border d-inline-flex flex-wrap gap-2 border-0">
                {/* <button
                  className="primary-btn d-inline-flex"
                  onClick={handleAddOwner}
                >
                  <AddSharpIcon />
                  Add Owner
                </button> */}

                <div
                  className="btn-group user-activity-btn-grp gap-2 gap-md-0 flex-wrap"
                  role="group"
                  aria-label="Basic mixed styles example"
                >
                  <button
                    className={`tab-btn d-inline-flex justify-content-between align-items-center ${
                      isActiveOwnerClicked.current ? "bg-theme" : "bg-theme-white"
                    }`}
                    onClick={() => {
                      showActiveOwners();
                    }}
                  >
                    Active
                    <span
                      className={`review--number d-inline-flex align-items-center justify-content-center rounded-circle ms-2 ${
                        isActiveOwnerClicked.current
                          ? "bg-theme-white"
                          : "bg-theme"
                      }`}
                    >
                      {activeOwnerCount}
                    </span>
                  </button>

                  <button
                    className={`tab-btn d-inline-flex justify-content-between align-items-center ${
                      isDeletedOwnerClicked.current ? "bg-theme" : "bg-theme-white"
                    }`}
                    onClick={() => {
                      showDeletedUsers();
                    }}
                  >
                    Deleted
                    <span
                      className={`review--number d-inline-flex align-items-center justify-content-center rounded-circle ms-2 ${
                        isDeletedOwnerClicked.current
                          ? "bg-theme-white"
                          : "bg-theme"
                      }`}
                    >
                      {deleteOwnerCount}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
              <div className="blank-div">
                {selectionModel.length > 0 && !isDeletedOwnerClicked.current && (
                  <div>
                    <DeleteOwner
                      deleteOwner={selectionModel}
                      reloadTable={reloadTable}
                    />
                  </div>
                )}
              </div>
        </div>
        <div className="user-table-container">
          <div className="userTable">
            <DataGrid
              rows={tableDataWithIds}
              columns={columns}
              className="data-grid"
              initialState={{
                pagination: {
                  paginationModel: { page, pageSize },
                },
              }}
              pagination
              paginationModel={{ page, pageSize }}
              paginationMode="server"
              sortingMode="server"
              disableRowSelectionOnClick={true}
              rowCount={totalCount}
              pageSizeOptions={[10, 20, 50, 100]}
              localeText={localeText}
              checkboxSelection
              rowSelectionModel={selectionModel}
              onSortModelChange={handleSortChange}
              onPaginationModelChange={onPaginationModelChange}
              onRowSelectionModelChange={handleSelectionModelChange}
              loading={isLoading}
              slots={{
                loadingOverlay: DataGridLoader,
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default BarrelOwnerListing;
